// Loads the vendored bim-hub-model-storage 0.1.0 artifact (see
// src/vendor/bim-hub-model-storage-0.1.0/manifest.json and NOTICE.md) and exposes a narrow, typed
// surface for turning an already-built D3 commit payload JSON string (`D3CommitPayload.payloadJson`
// -- see src/domain/d3-commit-payload.ts) into the manifest and content-addressed chunk bytes
// BIM-Hub's `bim-hub.model-chunks/1` storage expects. This module never calls the network and never
// sees an Identity or BIM-Hub token: the result is handed to the Workbench BFF
// (functions/d3/save.ts / src/server/d3-save.ts), which is the only place a BIM-Hub token exists --
// see bim-hub-integration/answers/2026-08-11-d3-first-live-save-go.md condition 1.
//
// Loading discipline mirrors src/integration/ifc-mesh-wasm.ts: lazy dynamic import, only on first
// use (i.e. only when a save is actually attempted), never eagerly at page load.
//
// Digest check differs from ifc-mesh-wasm.ts/ifc-wasm.ts on purpose. Those hand a URL straight to
// wasm-bindgen's own fetch-based loader (`mod.default(wasmUrl)`), which instantiates whatever bytes
// the server happens to answer with. Condition 2 of the GO answer above requires this artifact to
// refuse to START if its WASM digest does not match the pin -- not merely be flagged after the
// fact -- so this module fetches the raw bytes itself first, hashes them, and only instantiates on
// a match. A digest mismatch here never reaches WebAssembly.instantiate.
//
// `hydrateAndUpdateModelStoragePlan` (added 2026-08-13, the D3 "second save" fix -- see
// src/server/d3-save.ts's own module doc comment for the full root-cause story) is the ONLY correct
// way to build a save against a known target with a real prior revision: `buildModelStoragePlan`'s
// `.create()` always writes `manifest.base_revision: null`, which is only valid for a model's
// first-ever revision. `src/vendor/bim-hub-model-storage-0.1.0/NOTICE.md`'s "Scope boundary" section
// still describes the OLD (incorrect) design where `hydrate`/`update` were vendored but unused --
// that reasoning is now known wrong (see this file's PR/report trail) and that file has been updated
// to match.
import wasmUrl from "../vendor/bim-hub-model-storage-0.1.0/bim_hub_model_storage_bg.wasm?url";
import { sha256Hex } from "./local-ifc";

export const BIM_HUB_MODEL_STORAGE_WASM_SHA256 =
  "4f44f19d7a01480678a48f408e40a43492cbf0ba8bbcd1dd71ef6c772a4b5d07";

export interface ModelStorageChunk {
  readonly digest: string;
  readonly bytes: Uint8Array;
}

export interface ModelStoragePlan {
  readonly manifestJson: string;
  readonly manifestSha256: string;
  readonly payloadSha256: string;
  readonly payloadBytes: number;
  readonly revision: number;
  readonly chunks: readonly ModelStorageChunk[];
}

export type ModelStorageRefusalReason = "wasm-fetch-failed" | "wasm-digest-mismatch" | "wasm-init-failed" | "session-build-failed";

export interface ModelStorageRefusal {
  readonly ok: false;
  readonly reason: ModelStorageRefusalReason;
  readonly detail: string;
}

export type ModelStorageResult = { readonly ok: true; readonly plan: ModelStoragePlan } | ModelStorageRefusal;

interface WasmModelStorageSession {
  chunkBytes(digest: string): Uint8Array;
  contentDigests(): string[];
  update(revisionValue: number, planJson: string): WasmModelStorageSession;
  readonly manifestJson: string;
  readonly manifestSha256: string;
  readonly payloadBytes: number;
  readonly payloadSha256: string;
  readonly revision: number;
  free(): void;
}

/** What `.hydrate()`'s `content_objects` parameter (typed `Array<any>` in the vendored `.d.ts`,
 *  since wasm-bindgen erases the element type) actually expects at runtime -- confirmed against
 *  Editor2's own `hydrateNativeModelSnapshot` call site (`bim-hub/frontend/src/lib/
 *  model-storage-builder.ts`), the exact reference the read contract named as copyable: a plain
 *  `{digest, bytes}` object per content ref, not the wire's snake_case shape. */
interface WasmContentObject {
  readonly digest: string;
  readonly bytes: Uint8Array;
}

interface WasmModelStorageCtor {
  create(projectId: string, modelId: string, revisionValue: number, planJson: string): WasmModelStorageSession;
  hydrate(manifestJson: string, manifestSha256: string, contentObjects: readonly WasmContentObject[]): WasmModelStorageSession;
}

interface WasmModelStorageModule {
  readonly default: (input: BufferSource) => Promise<unknown>;
  readonly ModelStorageSession: WasmModelStorageCtor;
}

class WasmDigestMismatchError extends Error {}
class WasmFetchError extends Error {}

async function fetchVerifiedWasmBytes(fetcher: typeof fetch): Promise<ArrayBuffer> {
  let response: Response;
  try {
    response = await fetcher(wasmUrl);
  } catch (cause) {
    throw new WasmFetchError(`bim-hub-model-storage WASM fetch failed: ${cause instanceof Error ? cause.message : String(cause)}`);
  }
  if (!response.ok) throw new WasmFetchError(`bim-hub-model-storage WASM fetch failed: HTTP ${response.status}`);
  const bytes = await response.arrayBuffer();
  const digest = await sha256Hex(new Uint8Array(bytes));
  if (digest !== BIM_HUB_MODEL_STORAGE_WASM_SHA256) {
    throw new WasmDigestMismatchError(
      `bim-hub-model-storage WASM digest mismatch: expected ${BIM_HUB_MODEL_STORAGE_WASM_SHA256}, got ${digest}`
    );
  }
  return bytes;
}

let wasmModulePromise: Promise<WasmModelStorageModule> | null = null;

function loadWasm(fetcher: typeof fetch): Promise<WasmModelStorageModule> {
  if (wasmModulePromise === null) {
    wasmModulePromise = (async () => {
      const bytes = await fetchVerifiedWasmBytes(fetcher);
      const mod = (await import("../vendor/bim-hub-model-storage-0.1.0/bim_hub_model_storage.js")) as unknown as WasmModelStorageModule;
      await mod.default(bytes);
      return mod;
    })().catch((cause: unknown) => {
      wasmModulePromise = null;
      throw cause;
    });
  }
  return wasmModulePromise;
}

/** Reads the finished `ModelStoragePlan` back off a session, without freeing it -- shared by
 *  `buildModelStoragePlan` and `hydrateAndUpdateModelStoragePlan` so both fail the same way
 *  (`"session-build-failed"`) if the core itself refuses partway through. */
function planFromSession(session: WasmModelStorageSession): ModelStoragePlan {
  const digests = session.contentDigests();
  const chunks: ModelStorageChunk[] = digests.map((digest) => ({ digest, bytes: session.chunkBytes(digest) }));
  return {
    manifestJson: session.manifestJson,
    manifestSha256: session.manifestSha256,
    payloadSha256: session.payloadSha256,
    payloadBytes: session.payloadBytes,
    revision: session.revision,
    chunks
  };
}

/**
 * Builds a ModelStorage snapshot for `planJson` (the existing `D3CommitPayload.payloadJson`,
 * treated as opaque well-formed JSON by this module and by BIM-Hub alike -- see
 * `ModelStorageSession.create`'s own doc comment in the vendored `.d.ts`) at `revisionValue`.
 *
 * Fails closed -- returns a typed refusal, never throws -- when the WASM fetch fails, when the
 * fetched bytes' digest does not match the pin, when WASM init otherwise fails, or when the core
 * itself refuses to build a session.
 *
 * Only correct for a model's FIRST-EVER revision: `.create()` always writes `manifest.base_revision:
 * null` (see `src/server/d3-save.ts`'s own module doc comment for the full story). Every save
 * against a KNOWN target with a real prior revision must go through `hydrateAndUpdateModelStoragePlan`
 * below instead.
 */
export async function buildModelStoragePlan(
  projectId: string,
  modelId: string,
  revisionValue: number,
  planJson: string,
  fetcher: typeof fetch = (...args) => fetch(...args)
): Promise<ModelStorageResult> {
  let wasm: WasmModelStorageModule;
  try {
    wasm = await loadWasm(fetcher);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : String(cause);
    const reason: ModelStorageRefusalReason =
      cause instanceof WasmDigestMismatchError ? "wasm-digest-mismatch" : cause instanceof WasmFetchError ? "wasm-fetch-failed" : "wasm-init-failed";
    return { ok: false, reason, detail: message };
  }
  let session: WasmModelStorageSession;
  try {
    session = wasm.ModelStorageSession.create(projectId, modelId, revisionValue, planJson);
  } catch (cause) {
    return { ok: false, reason: "session-build-failed", detail: cause instanceof Error ? cause.message : String(cause) };
  }
  try {
    return { ok: true, plan: planFromSession(session) };
  } catch (cause) {
    return { ok: false, reason: "session-build-failed", detail: cause instanceof Error ? cause.message : String(cause) };
  } finally {
    session.free();
  }
}

/**
 * The read-hydrate-update fix's browser-side half: rebuilds the exact BIM-Hub-verified session at
 * `baseManifestJson`/`baseManifestSha256` (already digest-verified by `getModelManifestAtRevision`
 * server-side -- this call never re-fetches or re-verifies anything itself, it only reconstructs the
 * Rust-side session state Rust needs to derive a correct `base_revision`), then advances it to
 * `revisionValue` with `planJson` exactly like `.create()` would, except the resulting manifest's
 * `base_revision` is now the hydrated session's own revision (BIM-Hub's `build_snapshot` derives it
 * from `previous.map(|snapshot| snapshot.manifest.revision)` -- only `.update()` supplies a
 * `previous`; `.create()` never can). `contentObjects` must be exactly `{metadata.digest} ∪
 * {every collections[].pages[].digest}` from the base manifest -- see
 * `src/domain/model-chunk-manifest.ts`'s `manifestContentDigests`, and `functions/d3/
 * read-model-snapshot.ts`, which is what supplies them in practice.
 *
 * Fails closed the same way `buildModelStoragePlan` does. Does not itself compute `page_changes` --
 * the caller parses `baseManifestJson` via `parseModelChunkManifest` (independent of this WASM call,
 * using the same raw bytes the server already digest-verified) and diffs it against the returned
 * plan's own manifest via `diffModelChunkPages`.
 */
export async function hydrateAndUpdateModelStoragePlan(
  baseManifestJson: string,
  baseManifestSha256: string,
  contentObjects: readonly ModelStorageChunk[],
  revisionValue: number,
  planJson: string,
  fetcher: typeof fetch = (...args) => fetch(...args)
): Promise<ModelStorageResult> {
  let wasm: WasmModelStorageModule;
  try {
    wasm = await loadWasm(fetcher);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : String(cause);
    const reason: ModelStorageRefusalReason =
      cause instanceof WasmDigestMismatchError ? "wasm-digest-mismatch" : cause instanceof WasmFetchError ? "wasm-fetch-failed" : "wasm-init-failed";
    return { ok: false, reason, detail: message };
  }
  let baseSession: WasmModelStorageSession;
  try {
    baseSession = wasm.ModelStorageSession.hydrate(baseManifestJson, baseManifestSha256, contentObjects);
  } catch (cause) {
    return { ok: false, reason: "session-build-failed", detail: cause instanceof Error ? cause.message : String(cause) };
  }
  try {
    let updatedSession: WasmModelStorageSession;
    try {
      updatedSession = baseSession.update(revisionValue, planJson);
    } catch (cause) {
      return { ok: false, reason: "session-build-failed", detail: cause instanceof Error ? cause.message : String(cause) };
    }
    try {
      return { ok: true, plan: planFromSession(updatedSession) };
    } catch (cause) {
      return { ok: false, reason: "session-build-failed", detail: cause instanceof Error ? cause.message : String(cause) };
    } finally {
      updatedSession.free();
    }
  } finally {
    baseSession.free();
  }
}
